// „Dodaj do ekranu": the one band that tells a resident the app can live on
// their home screen (Decision 50 as amended, #348).
//
// It sits under the topbar — 45 px of paper-2 with the footer's own ink rule
// under it — because the amendment says why it exists: the browser's own menu
// was too hard to find, and a row further down the list is another hiding
// place. Four things make it absent, and only the first three are this
// module's: the app is already a standalone window, the resident has dismissed
// it once and for good, or the browser offers no way in we can name honestly.
// The fourth is the desktop, and it belongs to the stylesheet rather than to a
// device test (§5.10, ruling 12), exactly as the help screen's keyboard line
// does: the band is in the DOM and `display: none` above 900 px. The rules are
// in `install.css`, which `src/style.css` imports beside every other part's.
//
// Where no band of ours will be drawn, the browser keeps its own install
// affordance: `preventDefault()` is called only on the offers this module
// really answers. And where the platform does not say plainly what it can do —
// a browser embedded in another app — the answer is no band, never an
// instruction whose menu is not in front of the resident.
import { INSTALL_ID, installBand, mount } from '../shell.ts';
import { memory } from '../state/memory.ts';

/** What the resident answered in the browser's own dialog. */
type Outcome = { outcome: 'accepted' | 'dismissed' };

/**
 * Chrome's offer to install, which the DOM lib does not declare. Keeping the
 * event is the whole Android path: `preventDefault()` stops the browser drawing
 * an affordance of its own, and the band's tap fires this one.
 *
 * `prompt()` may be called ONCE — a second call rejects with
 * `InvalidStateError` — and it resolves with the answer; the older browsers
 * that resolve nothing carry it on `userChoice` instead, so both are read.
 */
interface InstallPrompt extends Event {
  prompt: () => Promise<Outcome | undefined>;
  userChoice?: Promise<Outcome>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: InstallPrompt;
  }
}

/** The copy, verbatim from the approved mock-up. */
const COPY = {
  label: 'Dodaj do ekranu',
  /** iOS only, and only once the resident asks for it. */
  how: 'Udostępnij → Do ekranu początkowego',
  dismiss: 'Ukryj',
};

/** A hollow arrow onto a bar: one glyph for both platforms, ink and nothing else. */
const GLYPH = '⇪';

/**
 * The resident's „no" in this tab, for a browser whose storage refused the
 * write: in a private window `memory.install.dismiss()` is swallowed, and the
 * ✕ still has to hold for as long as this page is open.
 */
let hiddenHere = false;

/** What the browser can offer, as one value this module can be tested on. */
export type Env = {
  /** already an app on the home screen */
  standalone: boolean;
  /** this browser has been told once, and once is for ever */
  dismissed: boolean;
  /** a `beforeinstallprompt` is in hand */
  prompts: boolean;
  /** Safari on iOS or iPadOS, where the share sheet is the only way in */
  iosSafari: boolean;
};

/** Which band this browser gets, or none at all. */
export type Band = 'prompt' | 'ios' | null;

/**
 * The visibility rule (Decision 50 as amended, c and d first).
 *
 * A browser that offers the install itself always wins: one tap and no prose.
 * Safari's share sheet is the only other way in we can name, so every other
 * browser gets nothing — an instruction that does not fit the menu in front of
 * the resident is worse than no band at all.
 */
export function bandFor(env: Env): Band {
  if (env.standalone || env.dismissed) return null;
  if (env.prompts) return 'prompt';
  return env.iosSafari ? 'ios' : null;
}

/**
 * Safari on iOS or iPadOS, from the user agent and the touch points alone.
 *
 * Three conditions, and the first two are POSITIVE: iOS or iPadOS — which calls
 * itself a Macintosh, and only its touch points give it away — and then
 * `Version/` and `Safari/`, which Safari writes and a browser embedded in
 * another app does not. Only the third is a denylist. Where the agent does not
 * say, the answer is „no band": a wrapper that has no share sheet and is
 * promised one is the failure this whole module's rule is about, and there is
 * no cost to a resident in Safari's own browser being asked from the menu.
 */
export function iosSafari(ua: string, touchPoints: number): boolean {
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && touchPoints > 1);
  return ios && /Version\//.test(ua) && /Safari\//.test(ua) && !WRAPPED.test(ua);
}

/**
 * The browsers-inside-an-app: Facebook, Instagram, Line, X, WeChat, Google's
 * own, and the three iOS browsers that put their name in the agent. Every one
 * of them is WebKit on iOS and none of them draws Safari's share sheet.
 *
 * A denylist alone would be permanently incomplete — the next wrapper would
 * default to a promise it cannot keep — which is why it is the third of three
 * conditions above rather than the only one.
 */
const WRAPPED = /FBAN|FBAV|Instagram|Line\/|Twitter|MicroMessenger|GSA\/|CriOS|FxiOS|EdgiOS|OPiOS/;

/**
 * Whether the app is already its own window.
 *
 * Both readings, because neither covers both platforms: iOS Safari answered
 * `navigator.standalone` for years before it read a `display-mode` query, and
 * Chrome answers the query and not the flag.
 */
export function standaloneFrom(displayMode: boolean, iosFlag: boolean | undefined): boolean {
  return displayMode || iosFlag === true;
}

function standalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return standaloneFrom(matchMedia(STANDALONE).matches, nav.standalone);
}

const STANDALONE = '(display-mode: standalone)';
/**
 * The breakpoint the stylesheet draws the band under (§5.10, ruling 12), read
 * here for ONE decision and no other: whether to take the browser's own install
 * affordance away. Nothing about the band's own layout is decided in JS.
 */
const WIDE = '(min-width: 900px)';

type Parts = {
  root: HTMLElement;
  act: HTMLButtonElement;
  /** the iOS instruction; absent where the browser installs by itself */
  how: HTMLElement | null;
  dismiss: HTMLButtonElement;
};

/** The band, as the mock-up draws it. */
function draw(kind: 'prompt' | 'ios'): Parts {
  const root = document.createElement('div');
  root.className = 'install install--bar';
  root.id = INSTALL_ID;

  const glyph = document.createElement('span');
  glyph.className = 'install__glyph';
  glyph.setAttribute('aria-hidden', 'true');
  glyph.textContent = GLYPH;

  const text = document.createElement('span');
  text.className = 'install__text';
  const label = document.createElement('span');
  label.className = 'install__label';
  label.textContent = COPY.label;
  text.append(label);

  const act = document.createElement('button');
  act.className = 'install__act';
  act.type = 'button';
  let how: HTMLElement | null = null;
  if (kind === 'ios') {
    // The instruction is a disclosure: named as closed, and out of the
    // accessibility tree until it is open (`[hidden]`, src/style.css).
    act.setAttribute('aria-expanded', 'false');
    how = document.createElement('span');
    how.className = 'install__how';
    how.textContent = COPY.how;
    how.hidden = true;
    text.append(how);
  }
  act.append(glyph, text);

  const dismiss = document.createElement('button');
  dismiss.className = 'install__dismiss';
  dismiss.type = 'button';
  dismiss.setAttribute('aria-label', COPY.dismiss);
  dismiss.textContent = '✕';

  root.append(act, dismiss);
  return { root, act, how, dismiss };
}

function remove(): void {
  const up = installBand();
  if (!up) return;
  // The ✕ removes the very control that was just activated, and a keyboard or
  // screen-reader resident would be dropped at `<body>`, back at the top of the
  // document (§8). The bar above is where the band sat, so its first control is
  // where they go.
  if (up.contains(document.activeElement)) mount('topbar').querySelector('button')?.focus();
  up.remove();
  delete document.documentElement.dataset.install;
}

/**
 * The browser's own dialog, and what its answer means for the band.
 *
 * The offer is spent by the first `prompt()`: a second call rejects with
 * `InvalidStateError` and Chrome makes no new offer for this page load, so the
 * band goes with it rather than staying as a control that does nothing. What
 * „accepted" leaves behind is `appinstalled`'s business, and it says the same.
 */
async function ask(offered: InstallPrompt): Promise<void> {
  try {
    const answer = (await offered.prompt()) ?? (await offered.userChoice);
    // „No" to the DIALOG is not „no" to us: it is this tab's answer for its
    // next page load, never the permanent key the ✕ writes.
    if (answer?.outcome === 'dismissed') memory.install.cancel();
  } catch {
    /* a spent offer, or a browser that refuses to show one */
  }
  remove();
}

/**
 * Opens and closes the iOS instruction in place.
 *
 * The band keeps its 45 px in both states — the second line is 40 px and the
 * target around the first is 44 — so nothing under the resident's thumb moves
 * when they ask how (measured in the mock-up, and asserted in
 * `e2e/install.spec.ts`).
 */
function toggleHow({ act, how }: Parts): void {
  const open = act.getAttribute('aria-expanded') === 'true';
  act.setAttribute('aria-expanded', open ? 'false' : 'true');
  if (how) how.hidden = open;
}

/**
 * Puts the band under the topbar and wires it. One band, however many times the
 * browser makes its offer.
 */
function band(kind: 'prompt' | 'ios', onAct: (parts: Parts) => void): void {
  if (installBand()) return;
  const parts = draw(kind);
  parts.act.addEventListener('click', () => onAct(parts));
  parts.dismiss.addEventListener('click', () => {
    // Remembered before it is removed: a resident who dismisses it has answered
    // the question, and the answer outlives the reload (Decision 50 d). Both
    // ways, because in a private window the stored half is swallowed and the
    // band would come back in the tab they are looking at.
    hiddenHere = true;
    memory.install.dismiss();
    remove();
  });
  mount('topbar').after(parts.root);
  // The column is 45 px shorter than it was a moment ago, and on Android that
  // moment is long after the kreska sized the circle block. `<html>` is where
  // this app says such a thing (`data-map`, `data-ready`, `data-breath`), and
  // the kreska's own observer is already watching for one.
  document.documentElement.dataset.install = 'true';
}

/**
 * Mounts the band if this browser has anything to offer. Called before the
 * kreska sizes the circle block, so the 45 px are already off the column when
 * the first geometry is written (src/kreska/kreska.ts).
 */
export function mountInstall(): void {
  // Read ONCE, at boot. A cancelled dialog is this tab's answer for its next
  // page load; a browser that makes a second offer inside THIS one has changed
  // its own mind, and we take it — the resident said no to the dialog, not to
  // the band.
  const cancelledBefore = memory.install.cancelled();

  const env = (prompts: boolean): Env => ({
    standalone: standalone(),
    dismissed: hiddenHere || cancelledBefore || memory.install.dismissed(),
    prompts,
    iosSafari: iosSafari(navigator.userAgent, navigator.maxTouchPoints),
  });

  let offer: InstallPrompt | null = null;
  addEventListener('beforeinstallprompt', (event) => {
    // Decided BEFORE `preventDefault()`, and this is the whole reason the
    // breakpoint is read here: where no band of OURS will be drawn — dismissed,
    // standalone, or the desktop, where the stylesheet hides it — the browser
    // keeps its own install affordance. Decision 50 d ends our band, not the
    // browser's menu. The state is read again rather than remembered, because
    // the offer arrives after the page has loaded and a ✕ in between wins.
    if (matchMedia(WIDE).matches || bandFor(env(true)) !== 'prompt') return;
    event.preventDefault();
    offer = event;
    band('prompt', () => {
      // The offer is spent by one `prompt()`, so it is taken out of hand here
      // and a second tap can never reach it.
      const asked = offer;
      offer = null;
      if (asked) void ask(asked);
    });
  });
  // Installed from anywhere — this band or the browser's own menu. Nothing is
  // written down: the installed window is standalone and shows no band anyway,
  // and in the tab the resident installed from Chrome makes no further offer
  // while the app is installed, so the rule already yields nothing. An app
  // removed from the home screen is exactly who the band is for.
  addEventListener('appinstalled', remove);

  if (bandFor(env(false)) === 'ios') band('ios', toggleHow);
}
